import { useEffect, useRef, useState, useCallback } from "react";
import { api, getToken, relTime } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const PERM_STORAGE_KEY = "span_push_prompted_at";

// Surface the most recent in-app notification arrival across the app —
// /my-tasks renders an "Enable notifications" CTA when permission == 'default'.
function fireBrowserPush(notif) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const title = notif.title || "SPAN — new alert";
    const body = (notif.body || "").slice(0, 220);
    const tag = notif.notif_id || notif.id || `span-${Date.now()}`;
    const n = new Notification(title, {
      body,
      tag,
      icon: "/brand/span-logo.png",
      badge: "/brand/span-logo.png",
      silent: false,
    });
    n.onclick = () => {
      window.focus();
      if (notif.url) window.location.href = notif.url;
      else window.location.href = "/notifications";
      n.close();
    };
    // Mobile haptic — no-op on desktops without the API
    if (navigator.vibrate) navigator.vibrate([60, 40, 60]);
  } catch { /* ignore */ }
}

export default function useNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [permission, setPermission] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );
  const wsRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
      // First load: seed seenIds so we don't fire desktop pushes for historical items.
      if (!initializedRef.current) {
        (data.items || []).forEach((n) => {
          const id = n.notification_id || n.notif_id || n.id;
          if (id) seenIdsRef.current.add(id);
        });
        initializedRef.current = true;
      }
    } catch { /* ignore */ }
  }, []);

  const handleIncomingRef = useRef(null);

  // keep ref pointed at the latest handler so the WS callback never closes over a stale version
  useEffect(() => {
    handleIncomingRef.current = (notif) => {
      const id = notif.notification_id || notif.notif_id || notif.id;
      if (id && seenIdsRef.current.has(id)) return;
      if (id) seenIdsRef.current.add(id);
      fireBrowserPush({
        notif_id: id,
        title: notif.title,
        body: notif.body || (notif.severity ? `Severity: ${notif.severity}` : ""),
        url: notif.link,
      });
    };
  });

  useEffect(() => {
    refresh();
    const pollId = setInterval(refresh, 60000);
    try {
      const token = getToken();
      if (!token) return () => clearInterval(pollId);
      const wsUrl = BASE.replace(/^http/, "ws") + "/api/ws/notifications?token=" + encodeURIComponent(token);
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "notification") {
            const payload = msg.data || msg.notification || {};
            handleIncomingRef.current && handleIncomingRef.current(payload);
            refresh();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => {};
      ws.onclose = () => {};
      wsRef.current = ws;
    } catch { /* ignore */ }
    return () => {
      clearInterval(pollId);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [refresh]);

  const requestPermission = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return "unsupported";
    }
    if (Notification.permission === "granted" || Notification.permission === "denied") {
      setPermission(Notification.permission);
      return Notification.permission;
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      try { localStorage.setItem(PERM_STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
      if (result === "granted") {
        // Confirmation toast notification so the user immediately sees it works.
        fireBrowserPush({
          title: "SPAN notifications enabled",
          body: "You'll now get real-time alerts for new sightings, tasks and escalations.",
        });
      }
      return result;
    } catch {
      return "default";
    }
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    refresh();
  };
  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    refresh();
  };

  return { items, unread, refresh, markRead, markAllRead, relTime, permission, requestPermission };
}
